/**
 * BatchGenerateModal
 * "Generate All Gaps" flow:
 *   Stage 1 — Progress: polls batch-generate-status every 2s, shows progress bar
 *   Stage 2 — Review:   consolidated list of all generated questions,
 *                        grouped by sub_topic + pool_type, Approve/Discard per question
 *   Stage 3 — Storing:  calls batch-store with approved + passed questions
 *   Stage 4 — Done:     summary of stored counts
 *
 * Regenerate is not available in batch mode (use per-sub-topic UniversalGenerateModal instead).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  X, CheckCircle, XCircle, AlertTriangle, ChevronDown, Zap, RefreshCw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function apiFetch(path, token, opts = {}) {
  return fetch(`${API}/api${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
}

// ── Shared sub-components ────────────────────────────────────────────────────

const ET_STYLES = {
  Foundational:       { bg: "#fee2e2", text: "#991b1b" },
  Conceptual:         { bg: "#fed7aa", text: "#9a3412" },
  Procedural:         { bg: "#ede9fe", text: "#5b21b6" },
  Careless:           { bg: "#fef9c3", text: "#854d0e" },
  Overgeneralisation: { bg: "#ccfbf1", text: "#134e4a" },
  Notation:           { bg: "#dbeafe", text: "#1e40af" },
  Mixed:              { bg: "#f3f4f6", text: "#374151" },
};

function ErrorTypeBadge({ type }) {
  if (!type) return null;
  const s = ET_STYLES[type] || { bg: "#f3f4f6", text: "#374151" };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold ml-1"
      style={{ background: s.bg, color: s.text }}>{type}</span>
  );
}

function DiffBadge({ diff }) {
  const map = {
    L1_EASY:         { label: "L1", bg: "#d1fae5", text: "#065f46" },
    L2_MEDIUM_PROC:  { label: "L2", bg: "#dbeafe", text: "#1e40af" },
    L3_MEDIUM_DEPTH: { label: "L3", bg: "#ede9fe", text: "#5b21b6" },
    L4_HARD_PROC:    { label: "L4", bg: "#fee2e2", text: "#991b1b" },
    L5_HARD_DEPTH:   { label: "L5", bg: "#fce7f3", text: "#9d174d" },
    Easy:   { label: "Easy",   bg: "#d1fae5", text: "#065f46" },
    Medium: { label: "Medium", bg: "#dbeafe", text: "#1e40af" },
    Hard:   { label: "Hard",   bg: "#fee2e2", text: "#991b1b" },
  };
  const c = map[diff] || { label: diff || "—", bg: "#f3f4f6", text: "#374151" };
  return <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
    style={{ background: c.bg, color: c.text }}>{c.label}</span>;
}

const POOL_COLORS = {
  WarmUp:    { color: "#2563eb", light: "#dbeafe" },
  "Pool A":  { color: "#7c3aed", light: "#ede9fe" },
  "Pool B":  { color: "#b45309", light: "#fef3c7" },
  "Cold Pool": { color: "#059669", light: "#d1fae5" },
};

// ── BatchQuestionCard ─────────────────────────────────────────────────────────

function BatchQuestionCard({ q, validation, poolType, approved, onApprove, onDiscard, qIndex }) {
  const [showExpl, setShowExpl] = useState(false);
  const passed    = validation?.passed;
  const errors    = validation?.errors || [];
  const opts      = q.options || {};
  const isWarmUp  = poolType === "WarmUp";

  return (
    <div
      className="rounded-xl border mb-3 overflow-hidden"
      style={{ borderColor: passed === false ? "#fca5a5" : approved ? "#6ee7b7" : "#e5e7eb" }}
      data-testid={`batch-qcard-${qIndex}`}
    >
      {/* Card header */}
      <div className="flex items-center justify-between px-4 py-2.5"
        style={{ background: passed === false ? "#fff1f2" : approved ? "#f0fdf4" : "#f9fafb" }}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-gray-400">Q{qIndex + 1}</span>
          {q.difficulty && <DiffBadge diff={q.difficulty} />}
          {passed ? (
            <span className="flex items-center gap-0.5 text-xs text-emerald-700 font-semibold">
              <CheckCircle size={11} /> Passed
            </span>
          ) : (
            <span className="flex items-center gap-0.5 text-xs text-red-600 font-semibold">
              <XCircle size={11} /> Failed
            </span>
          )}
        </div>
        {passed && (
          <button
            onClick={approved ? onDiscard : onApprove}
            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg"
            style={approved
              ? { background: "#d1fae5", color: "#065f46" }
              : { background: "#f3f4f6", color: "#6b7280" }}
            data-testid={`batch-approve-${qIndex}`}
          >
            {approved ? <><CheckCircle size={11} /> Approved</> : <><XCircle size={11} /> Discarded</>}
          </button>
        )}
        {!passed && (
          <span className="text-xs text-red-500 font-medium px-2">Auto-discarded</span>
        )}
      </div>

      {/* Validation errors */}
      {errors.length > 0 && (
        <div className="mx-4 mt-2.5 bg-red-50 border border-red-200 rounded-lg p-2.5">
          {errors.map((e, i) => (
            <p key={i} className="text-xs text-red-600">• {e}</p>
          ))}
        </div>
      )}

      {/* Question text */}
      <div className="px-4 pt-3 pb-2">
        <p className="text-sm text-gray-900 font-medium leading-snug">{q.question_text}</p>
      </div>

      {/* Options */}
      <div className="px-4 pb-3 space-y-1.5">
        {Object.entries(opts).sort().map(([letter, opt]) => {
          const label     = !isWarmUp ? q.option_labels?.[letter] : null;
          const isCorrect = letter === q.correct_answer;
          return (
            <div key={letter}
              className="flex items-start gap-2 p-2 rounded-lg"
              style={{
                background: isCorrect ? "#f0fdf4" : "#f9fafb",
                border:     `1px solid ${isCorrect ? "#6ee7b7" : "#e5e7eb"}`,
              }}
            >
              <span className="flex-shrink-0 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                style={{ background: isCorrect ? "#10b981" : "#e5e7eb", color: isCorrect ? "white" : "#6b7280" }}>
                {letter}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-1">
                  <span className="text-xs text-gray-800">{opt}</span>
                  {isCorrect ? (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Correct</span>
                  ) : (
                    !isWarmUp && label?.error_type && <ErrorTypeBadge type={label.error_type} />
                  )}
                </div>
                {!isWarmUp && label?.note && (
                  <p className="text-xs text-gray-400 italic mt-0.5">{label.note}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Explanation toggle */}
      <div className="px-4 pb-3">
        <button onClick={() => setShowExpl(v => !v)}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
          <ChevronDown size={12} style={{ transform: showExpl ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
          {showExpl ? "Hide" : "Show"} explanation
        </button>
        {showExpl && (
          <p className="text-xs text-gray-600 mt-1.5 leading-relaxed bg-gray-50 p-2.5 rounded border border-gray-200">
            {q.explanation}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Sub-topic group card ──────────────────────────────────────────────────────

function SubTopicGroup({ item, globalIndex, approved, setApproved, setDiscarded }) {
  const [open, setOpen] = useState(false);
  const { sub_topic, grade, subject, pool_type, questions = [], status } = item;
  const pl      = POOL_COLORS[pool_type] || POOL_COLORS["Pool A"];
  const passed  = questions.filter(q => q.validation?.passed);
  const nApproved = questions.filter((_, i) => approved[`${globalIndex}-${i}`]).length;

  const approveAll = () => {
    setApproved(prev => {
      const n = { ...prev };
      questions.forEach((q, i) => { if (q.validation?.passed) n[`${globalIndex}-${i}`] = true; });
      return n;
    });
    setDiscarded(prev => {
      const n = { ...prev };
      questions.forEach((_, i) => { delete n[`${globalIndex}-${i}`]; });
      return n;
    });
  };

  const discardAll = () => {
    setDiscarded(prev => {
      const n = { ...prev };
      questions.forEach((_, i) => { n[`${globalIndex}-${i}`] = true; });
      return n;
    });
    setApproved(prev => {
      const n = { ...prev };
      questions.forEach((_, i) => { delete n[`${globalIndex}-${i}`]; });
      return n;
    });
  };

  return (
    <div className="rounded-xl border mb-3 overflow-hidden" style={{ borderColor: "#e5e7eb" }}>
      <button className="w-full flex items-center justify-between px-5 py-3.5 bg-white hover:bg-gray-50"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2.5 flex-wrap text-left">
          <span className="inline-block px-2 py-0.5 rounded text-xs font-bold flex-shrink-0"
            style={{ background: pl.light, color: pl.color }}>{pool_type}</span>
          <span className="text-sm font-semibold text-gray-900">{sub_topic}</span>
          <span className="text-xs text-gray-400">{grade} · {subject}</span>
          {status === "error" && (
            <span className="text-xs text-red-500 font-semibold flex items-center gap-1">
              <AlertTriangle size={12} /> Generation failed
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          <span className="text-xs text-emerald-700 font-semibold">{nApproved}/{questions.length} approved</span>
          <span className="text-xs text-gray-400">{passed.length} passed val.</span>
          <ChevronDown size={14} className="text-gray-400"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
        </div>
      </button>

      {open && (
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          {/* Group bulk actions */}
          <div className="flex items-center gap-3 px-5 py-2.5 bg-gray-50">
            <button onClick={approveAll}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{ background: "#d1fae5", color: "#065f46" }}>
              Approve All Passed
            </button>
            <button onClick={discardAll}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{ background: "#fee2e2", color: "#991b1b" }}>
              Discard All
            </button>
          </div>

          <div className="px-5 py-4">
            {questions.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">No questions generated for this item.</p>
            )}
            {questions.map((entry, qi) => (
              <BatchQuestionCard
                key={qi}
                q={entry.question || entry}
                validation={entry.validation}
                poolType={pool_type}
                approved={!!approved[`${globalIndex}-${qi}`]}
                onApprove={() => setApproved(prev => ({ ...prev, [`${globalIndex}-${qi}`]: true }))}
                onDiscard={() => {
                  setDiscarded(prev => ({ ...prev, [`${globalIndex}-${qi}`]: true }));
                  setApproved(prev => { const n = { ...prev }; delete n[`${globalIndex}-${qi}`]; return n; });
                }}
                qIndex={qi}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

const POOL_FILTERS = ["All", "WarmUp", "Pool A", "Pool B"];

export default function BatchGenerateModal({ gapItems, token, onClose, onStored }) {
  const [stage,       setStage]       = useState("starting");
  const [batchId,     setBatchId]     = useState(null);
  const [progress,    setProgress]    = useState({ total: 0, completed: 0, generated: 0, passed: 0 });
  const [items,       setItems]       = useState([]);   // all batch result items
  const [approved,    setApproved]    = useState({});   // {"groupIdx-qIdx": bool}
  const [discarded,   setDiscarded]   = useState({});
  const [poolFilter,  setPoolFilter]  = useState("All");
  const [errorMsg,    setErrorMsg]    = useState("");
  const [budgetError, setBudgetError] = useState(null); // {current_cost, max_budget, required_topup}
  const [storeResult, setStoreResult] = useState(null);
  const [storedItems, setStoredItems] = useState([]);   // items submitted to batch-store

  const pollRef = useRef(null);

  // Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !["starting", "progress", "storing"].includes(stage)) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [stage, onClose]);

  // Start batch on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/admin/cr/batch-generate-start", token, {
          method: "POST",
          body: JSON.stringify({ items: gapItems }),
        });
        let d = {}; try { d = await res.json(); } catch (_) {}
        if (res.status === 402 && d.detail?.error_type === "budget_exceeded") {
          setBudgetError(d.detail);
          setStage("error");
          return;
        }
        if (!res.ok) throw new Error(d.detail || "Failed to start batch");
        setBatchId(d.batch_id);
        setProgress({ total: d.total, completed: 0, generated: 0, passed: 0 });
        setStage("progress");
      } catch (err) {
        setErrorMsg(err.message);
        setStage("error");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for progress
  useEffect(() => {
    if (stage !== "progress" || !batchId) return;

    const poll = async () => {
      try {
        const res = await apiFetch(`/admin/cr/batch-generate-status/${batchId}`, token);
        let d = {}; try { d = await res.json(); } catch (_) {}
        if (!res.ok) return;

        setProgress({ total: d.total, completed: d.completed, generated: d.generated, passed: d.passed });

        if (d.status === "done") {
          clearInterval(pollRef.current);
          const allItems = d.items || [];
          setItems(allItems);

          // Default: approve all passed
          const ap = {}, dc = {};
          allItems.forEach((item, gi) => {
            (item.questions || []).forEach((entry, qi) => {
              if (entry.validation?.passed) ap[`${gi}-${qi}`] = true;
              else dc[`${gi}-${qi}`] = true;
            });
          });
          setApproved(ap);
          setDiscarded(dc);
          setStage("review");
        }
      } catch (_) {}
    };

    pollRef.current = setInterval(poll, 2000);
    poll(); // immediate first poll
    return () => clearInterval(pollRef.current);
  }, [stage, batchId, token]);

  // Build store payload
  const getStoreItems = useCallback(() => {
    const result = [];
    items.forEach((item, gi) => {
      (item.questions || []).forEach((entry, qi) => {
        const key = `${gi}-${qi}`;
        if (approved[key] && entry.validation?.passed) {
          result.push({
            question:  entry.question || entry,
            pool_type: item.pool_type,
            grade:     item.grade,
            subject:   item.subject,
            sub_topic: item.sub_topic,
          });
        }
      });
    });
    return result;
  }, [items, approved]);

  const doStore = async () => {
    const storeItems = getStoreItems();
    if (storeItems.length === 0) return;
    setStoredItems(storeItems);
    setStage("storing");
    try {
      const res = await apiFetch("/admin/cr/batch-store", token, {
        method: "POST",
        body: JSON.stringify({ items: storeItems }),
      });
      let d = {}; try { d = await res.json(); } catch (_) {}
      if (!res.ok) throw new Error(d.detail || "Store failed");
      setStoreResult(d);
      setStage("done");
      onStored(d.stored || storeItems.length);
    } catch (err) {
      setErrorMsg(err.message);
      setStage("error");
    }
  };

  // Filtered items for review
  const filteredItems = items.filter(item =>
    poolFilter === "All" || item.pool_type === poolFilter
  );

  const totalApproved = getStoreItems().length;
  const totalGenerated = items.reduce((s, it) => s + (it.questions?.length || 0), 0);
  const totalPassed   = items.reduce((s, it) => s + (it.questions || []).filter(q => q.validation?.passed).length, 0);
  const bulkApproveAll = () => {
    const ap = { ...approved };
    items.forEach((item, gi) => {
      (item.questions || []).forEach((entry, qi) => {
        if (entry.validation?.passed) ap[`${gi}-${qi}`] = true;
      });
    });
    setApproved(ap);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const renderLoading = (msg, sub) => (
    <div className="bg-white rounded-2xl p-10 max-w-sm w-full text-center shadow-2xl">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div className="w-10 h-10 border-2 border-t-transparent rounded-full mx-auto mb-5"
        style={{ borderColor: "#7c3aed", borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
      <h3 className="font-bold text-gray-900 mb-2">{msg}</h3>
      <p className="text-sm text-gray-500">{sub}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      data-testid="batch-generate-modal">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Starting */}
      {stage === "starting" && renderLoading("Starting batch generation…", "Building queue of gap items…")}

      {/* Progress */}
      {stage === "progress" && (
        <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-md">
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full flex-shrink-0"
              style={{ borderColor: "#7c3aed", animation: "spin 0.8s linear infinite" }} />
            <div>
              <h3 className="font-bold text-gray-900">Batch Generation in Progress</h3>
              <p className="text-sm text-gray-500 mt-0.5">Generating via Claude · 3 concurrent calls</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
              <span>{progress.completed} / {progress.total} sub-topics</span>
              <span>{progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0}%</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden" style={{ background: "#e5e7eb" }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%`,
                  background: "#7c3aed",
                }} />
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6 justify-center text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{progress.generated}</div>
              <div className="text-xs text-gray-400">Generated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{progress.passed}</div>
              <div className="text-xs text-gray-400">Passed Validation</div>
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center mt-5">
            Do not close this window — generation is running in the background.
          </p>
        </div>
      )}

      {/* Review */}
      {stage === "review" && (
        <div className="bg-white rounded-2xl shadow-2xl flex flex-col"
          style={{ width: "100%", maxWidth: 800, maxHeight: "93vh" }}>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{ borderBottom: "1px solid #e5e7eb" }}>
            <div>
              <h2 className="font-bold text-gray-900 text-lg">Batch Review</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {items.length} sub-topics · {totalGenerated} questions generated · {totalPassed} passed validation
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X size={18} />
            </button>
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between px-6 py-3 flex-shrink-0 gap-3 flex-wrap"
            style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
            {/* Pool filter tabs */}
            <div className="flex items-center gap-1 flex-wrap">
              {POOL_FILTERS.map(f => (
                <button key={f} onClick={() => setPoolFilter(f)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={poolFilter === f
                    ? { background: "#7c3aed", color: "white" }
                    : { background: "#f3f4f6", color: "#374151" }}>
                  {f}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold" style={{ color: "#7c3aed" }}>
                {totalApproved} queued to save
              </span>
              <button onClick={bulkApproveAll}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ background: "#ede9fe", color: "#5b21b6" }}
                data-testid="batch-approve-all-btn">
                <CheckCircle size={12} /> Approve All Passed
              </button>
            </div>
          </div>

          {/* Items list */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {filteredItems.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-10">No items match this filter.</p>
            )}
            {filteredItems.map((item, fi) => {
              // Map filtered index back to global index
              const globalIndex = items.indexOf(item);
              return (
                <SubTopicGroup
                  key={`${item.grade}-${item.sub_topic}-${item.pool_type}`}
                  item={item}
                  globalIndex={globalIndex}
                  approved={approved}
                  setApproved={setApproved}
                  setDiscarded={setDiscarded}
                />
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{ borderTop: "1px solid #e5e7eb" }}>
            <button onClick={onClose}
              className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={doStore}
              disabled={totalApproved === 0}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{
                background: totalApproved > 0 ? "#7c3aed" : "#d1d5db",
                cursor: totalApproved > 0 ? "pointer" : "not-allowed",
              }}
              data-testid="batch-store-btn">
              <Zap size={14} />
              Store {totalApproved} Approved Question{totalApproved !== 1 ? "s" : ""}
            </button>
          </div>
        </div>
      )}

      {/* Storing */}
      {stage === "storing" && renderLoading(
        `Saving ${totalApproved} question${totalApproved !== 1 ? "s" : ""}…`,
        "Writing to fuel_questions database"
      )}

      {/* Done — Health Delta summary */}
      {stage === "done" && (() => {
        // Build per-grade breakdown from what was stored
        const gradeMap = {};
        storedItems.forEach(item => {
          const g = item.grade || "Unknown";
          if (!gradeMap[g]) gradeMap[g] = { subTopics: new Set(), WarmUp: 0, "Pool A": 0, "Pool B": 0 };
          gradeMap[g].subTopics.add(item.sub_topic);
          gradeMap[g][item.pool_type] = (gradeMap[g][item.pool_type] || 0) + 1;
        });
        const delta = Object.entries(gradeMap)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([grade, d]) => ({
            grade,
            subTopicsFilled: d.subTopics.size,
            warmup: d.WarmUp,
            poolA: d["Pool A"],
            poolB: d["Pool B"],
            total: d.WarmUp + d["Pool A"] + d["Pool B"],
          }));

        const totalStored     = storeResult?.stored ?? storedItems.length;
        const totalSubTopics  = [...new Set(storedItems.map(i => `${i.grade}|${i.sub_topic}`))].length;
        const totalWarmUp     = storedItems.filter(i => i.pool_type === "WarmUp").length;
        const totalPoolA      = storedItems.filter(i => i.pool_type === "Pool A").length;
        const totalPoolB      = storedItems.filter(i => i.pool_type === "Pool B").length;

        return (
          <div className="bg-white rounded-2xl shadow-2xl flex flex-col"
            style={{ width: "100%", maxWidth: 680, maxHeight: "90vh" }}>

            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4" style={{ borderBottom: "1px solid #e5e7eb" }}>
              <CheckCircle size={22} className="text-emerald-500 flex-shrink-0" />
              <div>
                <h2 className="font-bold text-gray-900">Batch Complete</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {totalStored} question{totalStored !== 1 ? "s" : ""} added across {totalSubTopics} sub-topic{totalSubTopics !== 1 ? "s" : ""}
                </p>
              </div>
            </div>

            {/* Overall totals strip */}
            <div className="grid grid-cols-4 gap-3 px-6 py-4" style={{ borderBottom: "1px solid #f3f4f6", background: "#fafafa" }}>
              {[
                { label: "Total Added",  value: totalStored,  color: "#059669" },
                { label: "WarmUp",       value: totalWarmUp,  color: "#2563eb" },
                { label: "Pool A",       value: totalPoolA,   color: "#7c3aed" },
                { label: "Pool B",       value: totalPoolB,   color: "#b45309" },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center">
                  <div className="text-2xl font-bold" style={{ color }}>{value}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{label}</div>
                </div>
              ))}
            </div>

            {/* Per-grade delta table */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {delta.length > 0 ? (
                <table className="w-full text-sm" data-testid="health-delta-table">
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                      {["Grade", "Sub-topics Filled", "WarmUp", "Pool A", "Pool B", "Total"].map(h => (
                        <th key={h} className="text-left pb-2 px-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {delta.map(row => (
                      <tr key={row.grade} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td className="py-2.5 px-2 font-semibold text-gray-800">
                          {row.grade.replace("Grade ", "G")}
                        </td>
                        <td className="py-2.5 px-2 text-gray-600 text-center">{row.subTopicsFilled}</td>
                        <td className="py-2.5 px-2 text-center">
                          {row.warmup > 0
                            ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700">{row.warmup}</span>
                            : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          {row.poolA > 0
                            ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-50 text-purple-700">{row.poolA}</span>
                            : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          {row.poolB > 0
                            ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700">{row.poolB}</span>
                            : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="py-2.5 px-2 text-center font-bold text-gray-900">{row.total}</td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr style={{ borderTop: "2px solid #e5e7eb", background: "#f9fafb" }}>
                      <td className="py-2.5 px-2 font-bold text-gray-900">All Grades</td>
                      <td className="py-2.5 px-2 font-bold text-gray-700 text-center">{totalSubTopics}</td>
                      <td className="py-2.5 px-2 text-center font-bold text-blue-700">{totalWarmUp || "—"}</td>
                      <td className="py-2.5 px-2 text-center font-bold" style={{ color: "#7c3aed" }}>{totalPoolA || "—"}</td>
                      <td className="py-2.5 px-2 text-center font-bold" style={{ color: "#b45309" }}>{totalPoolB || "—"}</td>
                      <td className="py-2.5 px-2 text-center font-bold text-emerald-700">{totalStored}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No questions were stored.</p>
              )}

              {storeResult?.errors?.length > 0 && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1">
                    {storeResult.errors.length} store error{storeResult.errors.length !== 1 ? "s" : ""}:
                  </p>
                  {storeResult.errors.slice(0, 5).map((e, i) => (
                    <p key={i} className="text-xs text-amber-600">• {e}</p>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4" style={{ borderTop: "1px solid #e5e7eb" }}>
              <button onClick={onClose}
                className="w-full px-6 py-2.5 rounded-lg text-sm font-semibold text-white"
                style={{ background: "#7c3aed" }}
                data-testid="batch-done-btn">
                Done
              </button>
            </div>
          </div>
        );
      })()}

      {/* Error */}
      {stage === "error" && (
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl">
          {budgetError ? (
            <>
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center"
                  style={{ background: "#fef3c7" }}>
                  <span style={{ fontSize: 20 }}>💳</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">LLM Budget Exhausted</h3>
                  <p className="text-sm text-amber-700 mt-1">{budgetError.message}</p>
                </div>
              </div>
              <div className="rounded-xl p-4 mb-5 space-y-2 text-sm"
                style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                <div className="flex justify-between">
                  <span className="text-gray-500">Current spend</span>
                  <span className="font-bold text-red-600">
                    ${budgetError.current_cost != null ? budgetError.current_cost.toFixed(4) : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Budget limit</span>
                  <span className="font-semibold text-gray-700">
                    ${budgetError.max_budget != null ? budgetError.max_budget.toFixed(4) : "—"}
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between" style={{ borderColor: "#fde68a" }}>
                  <span className="font-semibold text-gray-700">Recommended top-up</span>
                  <span className="font-bold text-emerald-700">
                    +${budgetError.required_topup != null ? budgetError.required_topup.toFixed(2) : "—"}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                Go to <strong>Profile → Universal Key → Add Balance</strong> (or enable auto top-up), then retry.
              </p>
            </>
          ) : (
            <div className="flex items-start gap-3 mb-5">
              <XCircle size={24} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900">Batch Failed</h3>
                <p className="text-sm text-red-600 mt-1">{errorMsg}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700">
              Close
            </button>
            <button onClick={() => window.location.reload()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: "#7c3aed" }}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
